import { supabase } from "../supabase";
import { getActiveOrganizationId, subscribeActiveOrg } from "../org";
import type { Receipt, ReceiptStore } from "./types";

const BUCKET = "receipts";
// 1 hour signed URLs. Re-issued on every full refresh of the snapshot.
const SIGNED_URL_TTL_SECONDS = 3600;

interface ReceiptRow {
  id: string;
  user_id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  storage_path: string;
  linked_expense_id: string | null;
  linked_invoice_id: string | null;
  uploaded_at: string;
}

const ROW_COLUMNS =
  "id, user_id, filename, mime_type, size_bytes, storage_path, linked_expense_id, linked_invoice_id, uploaded_at";

function extFromName(name: string): string {
  const dot = name.lastIndexOf(".");
  if (dot === -1 || dot === name.length - 1) return "bin";
  // Strip path-unsafe chars and lowercase. Defensive — most filenames are sane.
  const cleaned = name
    .slice(dot + 1)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .slice(0, 8);
  return cleaned || "bin";
}

async function signOne(storagePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(storagePath, SIGNED_URL_TTL_SECONDS);
  if (error || !data?.signedUrl) {
    // Louder diagnostics so bucket/RLS misconfigurations don't silently
    // surface as "images don't load". The error message distinguishes the
    // common failure modes (bucket missing, RLS denial, object missing).
    // eslint-disable-next-line no-console
    console.warn(
      `[receipts] failed to sign URL for "${storagePath}": ${error?.message ?? "unknown"}. ` +
        `Check that (a) the 'receipts' storage bucket exists and (b) storage.objects RLS allows the current user to SELECT it. ` +
        `Migration 0022_receipts_bucket_rls.sql sets these up.`,
    );
    return "";
  }
  return data.signedUrl;
}

function rowToReceipt(row: ReceiptRow, signedUrl: string): Receipt {
  return {
    id: row.id,
    filename: row.filename,
    mimeType: row.mime_type,
    sizeBytes: row.size_bytes,
    uploadedAt: row.uploaded_at,
    url: signedUrl,
    linkedExpenseId: row.linked_expense_id ?? undefined,
    linkedInvoiceId: row.linked_invoice_id ?? undefined,
  };
}

export function createSupabaseReceiptStore(): ReceiptStore {
  const listeners = new Set<() => void>();

  // Cached snapshot — MUST be a stable reference between mutations so that
  // useSyncExternalStore's Object.is check doesn't see a new array on every
  // render and trigger React error #185 (infinite re-render loop).
  let snapshot: Receipt[] = [];

  // Side table for storage paths, keyed by receipt id. Receipts only carry the
  // signed URL; deletion needs the raw storage_path for storage.remove().
  const storagePaths = new Map<string, string>();

  // Lazy load: don't fetch until the first subscribe/list. An in-flight
  // promise is shared across concurrent callers.
  let initialized = false;
  let loadPromise: Promise<void> | null = null;

  function emit(): void {
    for (const listener of listeners) {
      listener();
    }
  }

  function commit(next: Receipt[]): void {
    snapshot = next;
    emit();
  }

  async function loadFromServer(): Promise<void> {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user) {
      // Not signed in — empty snapshot. AuthGate handles the UX side.
      storagePaths.clear();
      commit([]);
      return;
    }

    const orgId = getActiveOrganizationId();
    if (!orgId) {
      storagePaths.clear();
      commit([]);
      return;
    }

    const { data: rows, error } = await supabase
      .from("receipts")
      .select(ROW_COLUMNS)
      .eq("organization_id", orgId)
      .order("uploaded_at", { ascending: false });

    if (error) {
      // eslint-disable-next-line no-console
      console.warn(`[receipts] list failed: ${error.message}`);
      storagePaths.clear();
      commit([]);
      return;
    }

    const typedRows = (rows ?? []) as ReceiptRow[];
    const signed = await Promise.all(
      typedRows.map((r) => signOne(r.storage_path)),
    );
    storagePaths.clear();
    for (const r of typedRows) {
      storagePaths.set(r.id, r.storage_path);
    }
    const next = typedRows.map((row, i) => rowToReceipt(row, signed[i] ?? ""));
    commit(next);
  }

  function ensureLoaded(): Promise<void> {
    if (initialized) return Promise.resolve();
    if (!loadPromise) {
      loadPromise = loadFromServer().finally(() => {
        initialized = true;
        loadPromise = null;
      });
    }
    return loadPromise;
  }

  // Refresh the snapshot whenever auth changes — sign-out clears it, sign-in
  // re-loads. Attached lazily on first subscribe so module import doesn't
  // trigger any network call.
  let authSubAttached = false;
  function attachAuthListener(): void {
    if (authSubAttached) return;
    authSubAttached = true;
    supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        initialized = false;
        storagePaths.clear();
        commit([]);
        return;
      }
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        initialized = false;
        // Fire and forget — listeners get notified via commit() inside.
        void ensureLoaded();
      }
    });
    subscribeActiveOrg(() => {
      initialized = false;
      storagePaths.clear();
      void ensureLoaded();
    });
  }

  return {
    list(): Receipt[] {
      // Kick off lazy load on first call. Don't await — UI re-renders when
      // the snapshot is populated.
      if (!initialized && !loadPromise) {
        void ensureLoaded();
      }
      return snapshot;
    },

    get(id: string): Receipt | undefined {
      return snapshot.find((r) => r.id === id);
    },

    async add(input: {
      file: File;
      linkedExpenseId?: string;
      linkedInvoiceId?: string;
    }): Promise<Receipt> {
      const { file, linkedExpenseId, linkedInvoiceId } = input;

      const { data: userData, error: userError } =
        await supabase.auth.getUser();
      if (userError || !userData?.user) {
        throw new Error("You must be signed in to upload a receipt.");
      }
      const userId = userData.user.id;

      const orgId = getActiveOrganizationId();
      if (!orgId) {
        throw new Error(
          "No active organization selected. Reload the page and try again.",
        );
      }

      const receiptId = crypto.randomUUID();
      const ext = extFromName(file.name);
      const storagePath = `${userId}/${receiptId}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(storagePath, file, {
          contentType: file.type || "application/octet-stream",
          upsert: false,
        });
      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      const { data: inserted, error: insertError } = await supabase
        .from("receipts")
        .insert({
          id: receiptId,
          organization_id: orgId,
          user_id: userId,
          filename: file.name,
          mime_type: file.type || "application/octet-stream",
          size_bytes: file.size,
          storage_path: storagePath,
          linked_expense_id: linkedExpenseId ?? null,
          linked_invoice_id: linkedInvoiceId ?? null,
        })
        .select(ROW_COLUMNS)
        .single();

      if (insertError || !inserted) {
        // Best-effort cleanup of the orphaned storage object.
        await supabase.storage.from(BUCKET).remove([storagePath]);
        throw new Error(
          `Failed to record receipt: ${insertError?.message ?? "unknown error"}`,
        );
      }

      const signedUrl = await signOne(storagePath);
      const insertedRow = inserted as ReceiptRow;
      const receipt = rowToReceipt(insertedRow, signedUrl);
      storagePaths.set(receipt.id, insertedRow.storage_path);
      // Mark as initialized so subsequent list() calls don't trigger a refetch
      // before the new receipt is in the snapshot.
      initialized = true;
      commit([receipt, ...snapshot]);
      return receipt;
    },

    remove(id: string): void {
      const target = snapshot.find((r) => r.id === id);
      if (!target) return;
      const storagePath = storagePaths.get(id);
      // Optimistic local update.
      const next = snapshot.filter((r) => r.id !== id);
      storagePaths.delete(id);
      commit(next);
      // Fire-and-forget remote delete. We log on failure but don't roll back —
      // a subsequent reload will reconcile via loadFromServer().
      void (async () => {
        const { error: dbErr } = await supabase
          .from("receipts")
          .delete()
          .eq("id", id);
        if (dbErr) {
          // eslint-disable-next-line no-console
          console.warn(`[receipts] delete row failed: ${dbErr.message}`);
        }
        if (storagePath) {
          const { error: storageErr } = await supabase.storage
            .from(BUCKET)
            .remove([storagePath]);
          if (storageErr) {
            // eslint-disable-next-line no-console
            console.warn(
              `[receipts] storage delete failed: ${storageErr.message}`,
            );
          }
        }
      })();
    },

    async refreshUrl(id: string): Promise<string | null> {
      const storagePath = storagePaths.get(id);
      if (!storagePath) return null;
      const fresh = await signOne(storagePath);
      if (!fresh) return null;
      // Update the snapshot so subsequent renders pick up the new URL.
      const idx = snapshot.findIndex((r) => r.id === id);
      if (idx === -1) return fresh;
      const current = snapshot[idx];
      if (!current) return fresh;
      const next = snapshot.slice();
      next[idx] = { ...current, url: fresh };
      commit(next);
      return fresh;
    },

    link(
      id: string,
      link: { linkedExpenseId?: string; linkedInvoiceId?: string },
    ): void {
      const idx = snapshot.findIndex((r) => r.id === id);
      if (idx === -1) return;
      const current = snapshot[idx];
      if (!current) return;
      const updated: Receipt = {
        ...current,
        linkedExpenseId: link.linkedExpenseId ?? current.linkedExpenseId,
        linkedInvoiceId: link.linkedInvoiceId ?? current.linkedInvoiceId,
      };
      const next = snapshot.slice();
      next[idx] = updated;
      commit(next);

      void supabase
        .from("receipts")
        .update({
          linked_expense_id: updated.linkedExpenseId ?? null,
          linked_invoice_id: updated.linkedInvoiceId ?? null,
        })
        .eq("id", id)
        .then(({ error }) => {
          if (error) {
            // eslint-disable-next-line no-console
            console.warn(`[receipts] link update failed: ${error.message}`);
          }
        });
    },

    subscribe(listener: () => void): () => void {
      attachAuthListener();
      // Trigger the initial load on first subscriber.
      if (!initialized && !loadPromise) {
        void ensureLoaded();
      }
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
